<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        Student Progress
        <i
          class="material-icons icon-16pt clickable-item"
          v-b-popover.hover.top="`View course enrollments and gradebook.`"
          >info</i
        >
      </h2>
      <div v-if="isSuperAdmin">
        <b-btn size="md" variant="outline-primary" :to="{ name: 'sa-student-progress-analytics' }">
          See Analytics
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search by student or course"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>

              <div v-if="!isTabSmallScreen" class="col-lg d-flex flex-wrap justify-content-end">
                <div class="row mx-auto mb-2 w-100 mt-2">
                  <div class="col-md-4">
                    <v-select
                      id="course_filter"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="courseFilter"
                      :reduce="c => c.id"
                      placeholder="Filter by Course"
                      :options="coursesList"
                      :loading="areCoursesLoading"
                      @input="onCourseChange"
                    >
                      <template #option="course">
                        <div class="course-opt">
                          <span class="course-opt__title">{{ course.title }}</span>
                          <template v-if="isSuperAdmin">
                            <b-badge
                              v-for="(tag, i) in courseTagBadges(course)"
                              :key="i"
                              variant="info"
                              pill
                              class="course-opt__tag"
                              >{{ tag }}</b-badge
                            >
                          </template>
                        </div>
                      </template>
                    </v-select>
                  </div>
                  <div class="col-md-4">
                    <v-select
                      id="status_filter"
                      class="form-control v-select-custom"
                      label="text"
                      v-model="statusFilter"
                      :reduce="s => s.value"
                      placeholder="Filter by Status"
                      :options="statusOptions"
                      @input="fetchEnrollments"
                    />
                  </div>
                  <div class="col-md-4">
                    <v-select
                      id="tag_filter"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="tagFilter"
                      :reduce="t => t.id"
                      placeholder="Filter by Tag"
                      :options="tagsList"
                      :loading="areTagsLoading"
                      @input="fetchEnrollments"
                    />
                  </div>
                </div>
              </div>

              <div v-if="isTabSmallScreen" class="col-lg d-flex flex-wrap justify-content-end">
                <div class="mb-2 mt-2" style="width: 100%">
                  <div class="mb-2">
                    <v-select
                      id="course_filter_sm"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="courseFilter"
                      :reduce="c => c.id"
                      placeholder="Filter by Course"
                      :options="coursesList"
                      :loading="areCoursesLoading"
                      @input="onCourseChange"
                    >
                      <template #option="course">
                        <div class="course-opt">
                          <span class="course-opt__title">{{ course.title }}</span>
                          <template v-if="isSuperAdmin">
                            <b-badge
                              v-for="(tag, i) in courseTagBadges(course)"
                              :key="i"
                              variant="info"
                              pill
                              class="course-opt__tag"
                              >{{ tag }}</b-badge
                            >
                          </template>
                        </div>
                      </template>
                    </v-select>
                  </div>
                  <div class="mb-2">
                    <v-select
                      id="status_filter_sm"
                      class="form-control v-select-custom"
                      label="text"
                      v-model="statusFilter"
                      :reduce="s => s.value"
                      placeholder="Filter by Status"
                      :options="statusOptions"
                      @input="fetchEnrollments"
                    />
                  </div>
                  <div>
                    <v-select
                      id="tag_filter_sm"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="tagFilter"
                      :reduce="t => t.id"
                      placeholder="Filter by Tag"
                      :options="tagsList"
                      :loading="areTagsLoading"
                      @input="fetchEnrollments"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div v-if="appliedFiltersSummary" class="mb-2 text-muted">
              Applied Filters: {{ appliedFiltersSummary }}
            </div>

            <b-table
              :fields="tableColumns"
              :items="enrollments"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              no-border-collapse
              no-local-sorting
              @row-clicked="handleRowClick"
              :tbody-tr-class="rowClass"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(student)="data">
                <div class="d-flex align-items-center">
                  <user-avatar class="mr-2" size="md" :fileUrl="get(data.item, 'student.avatar_url')" />
                  <strong>{{ get(data.item, 'student.name', '-') }}</strong>
                  <span v-if="get(data.item, 'student.is_active') === false" class="badge badge-secondary ml-2">
                    Inactive
                  </span>
                </div>
              </template>

              <template #cell(course)="data">
                <div>{{ get(data.item, 'course', '-') }}</div>
              </template>

              <template #cell(school)="data">
                <div>{{ resolveSchoolName(data.item) }}</div>
              </template>

              <template #cell(progress)="data">
                <div class="d-flex align-items-center" style="min-width: 120px">
                  <b-progress :max="100" height="12px" class="flex-grow-1 mr-2">
                    <b-progress-bar :value="get(data.item, 'progress_percent', 0)"></b-progress-bar>
                  </b-progress>
                  <small>{{ formatPercentTruncate(get(data.item, 'progress_percent', 0)) }}%</small>
                </div>
              </template>

              <template #cell(status)="data">
                <span class="badge" :class="statusBadgeClass(get(data.item, 'status'))">
                  {{ prettyStatus(get(data.item, 'status')) }}
                </span>
              </template>

              <template #cell(start_date)="data">
                <div>{{ formatDate(get(data.item, 'started_at')) }}</div>
              </template>
            </b-table>

            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalEnrollments"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="enrollments-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import UserAvatar from '@/components/User/UserAvatar.vue';
import { DEFAULT_PAGE_SIZE, USER_ROLE_TYPES } from '@/common/constants';
import persistListFilters from '@/mixins/persist-list-filters';

export default {
  components: {
    Pagination,
    UserAvatar,
  },
  extends: Page,
  mixins: [persistListFilters],
  // Page number is deliberately not persisted: the list always opens on page 1.
  filterKeys: ['searchTerm', 'courseFilter', 'statusFilter', 'tagFilter'],
  data() {
    return {
      isLoading: false,
      enrollments: [],
      totalEnrollments: 0,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      offset: 0,
      ordering: '-started_at',
      searchTerm: '',
      courseFilter: null,
      statusFilter: null,
      coursesList: [],
      areCoursesLoading: false,
      tagFilter: null,
      tagsList: [],
      areTagsLoading: false,
      windowWidth: window.innerWidth,
      statusOptions: [
        { value: 'enrolled', text: 'Enrolled' },
        { value: 'in_progress', text: 'In Progress' },
        { value: 'completed', text: 'Completed' },
        { value: 'dropped', text: 'Dropped' },
      ],
      USER_ROLE_TYPES,
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isSuperAdmin() {
      return this.getLoggedInUser.role === USER_ROLE_TYPES.SUPER_ADMIN;
    },
    tableColumns() {
      const studentStickyWidth = this.isTabSmallScreen ? '200px' : '260px';
      const progressStickyWidth = this.isTabSmallScreen ? '140px' : '180px';

      const columns = [
        {
          key: 'student',
          label: 'Student',
          stickyColumn: true,
          thStyle: { minWidth: studentStickyWidth, width: studentStickyWidth },
          class: 'text-left sticky-col sticky-col-student',
          thClass: 'sticky-col sticky-col-student',
          tdClass: 'sticky-col sticky-col-student',
        },
        { key: 'start_date', label: 'Start Date', thStyle: { minWidth: '160px' }, class: 'text-left' },
        {
          key: 'progress',
          label: 'Progress',
          thStyle: { minWidth: progressStickyWidth, width: progressStickyWidth },
          class: 'text-left',
          sortable: false,
        },
        { key: 'status', label: 'Status', thStyle: { minWidth: '10px' }, class: 'text-left' },
        { key: 'course', label: 'Course', thStyle: { minWidth: '180px' }, class: 'text-left' },
      ];

      if (this.isSuperAdmin) {
        columns.splice(2, 0, { key: 'school', label: 'School', thStyle: { minWidth: '180px' }, class: 'text-left' });
      }

      return columns;
    },
    appliedFiltersSummary() {
      const filters = [];

      if (this.searchTerm && this.searchTerm.trim()) {
        filters.push(`Search: ${this.searchTerm.trim()}`);
      }

      if (this.courseFilter) {
        const selectedCourse = this.coursesList.find(c => String(c.id) === String(this.courseFilter));
        filters.push(`Course: ${(selectedCourse && selectedCourse.title) || this.courseFilter}`);
      }

      if (this.statusFilter) {
        const selectedStatus = this.statusOptions.find(s => s.value === this.statusFilter);
        filters.push(`Status: ${(selectedStatus && selectedStatus.text) || this.statusFilter}`);
      }

      if (this.tagFilter) {
        const selectedTag = this.tagsList.find(t => t.id === this.tagFilter);
        filters.push(`Tag: ${(selectedTag && selectedTag.title) || this.tagFilter}`);
      }

      return filters.join(' | ');
    },
    isTabSmallScreen() {
      return this.windowWidth <= 767;
    },
  },
  methods: {
    get,
    courseTagBadges(course) {
      const tags = (course && course.custom_tags) || [];
      if (tags.length <= 2) return tags;
      return [...tags.slice(0, 2), `+${tags.length - 2}`];
    },
    ...mapActions('courseEnrollments', ['getCourseEnrollments']),
    ...mapActions('courses', ['getLmsCourses']),
    ...mapActions('attendance', ['getTags']),
    formatDate(val) {
      if (!val) return '-';
      try {
        const d = new Date(val);
        return d.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        });
      } catch (e) {
        return val;
      }
    },
    prettyStatus(st) {
      if (!st) return '-';
      const map = {
        enrolled: 'Enrolled',
        in_progress: 'In Progress',
        completed: 'Completed',
        dropped: 'Dropped',
      };
      return map[st] || st;
    },
    statusBadgeClass(st) {
      const m = {
        enrolled: 'badge-info',
        in_progress: 'badge-warning',
        completed: 'badge-success',
        dropped: 'badge-danger',
      };
      return m[st] || 'badge-secondary';
    },
    formatPercentTruncate(val) {
      const num = Number(val);
      if (!isFinite(num)) return '0.00';
      const truncated = Math.trunc(num * 100) / 100;
      return truncated.toFixed(2);
    },
    prettyGrade(g) {
      if (!g) return '-';
      const map = { passed: 'Passed', failed: 'Failed' };
      return map[g] || g;
    },
    async fetchCourses() {
      this.areCoursesLoading = true;
      try {
        const res = await this.getLmsCourses({
          // school__id: this.getLoggedInUser.linked_entity.id,
          pagination: false,
        });
        const list = res.data;
        this.coursesList = list || [];
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areCoursesLoading = false;
    },
    async fetchTags() {
      this.areTagsLoading = true;
      try {
        const selectedCourse = this.coursesList.find(c => String(c.id) === String(this.courseFilter));
        const programId = get(selectedCourse, 'program_category.id');
        const res = await this.getTags({
          ...(this.getLoggedInUser.linked_entity && { school__id: this.getLoggedInUser.linked_entity.id }),
          ...(programId && { program__id: programId }),
        });
        this.tagsList = get(res, 'data.data', []) || [];
      } catch (e) {
        this.tagsList = [];
      }
      if (this.tagFilter && !this.tagsList.some(t => t.id === this.tagFilter)) {
        this.tagFilter = null;
      }
      this.areTagsLoading = false;
    },
    async onCourseChange() {
      await this.fetchTags();
      this.fetchEnrollments();
    },
    async fetchEnrollments() {
      this.isLoading = true;
      try {
        const payload = await this.getCourseEnrollments({
          ...(this.getLoggedInUser.linked_entity && { school__id: this.getLoggedInUser.linked_entity.id }),
          limit: this.perPage,
          offset: this.offset,
          pagination: true,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.courseFilter ? { course__id: this.courseFilter } : {}),
          ...(this.statusFilter ? { status: this.statusFilter } : {}),
          ...(this.tagFilter ? { student__selected_tag: this.tagFilter } : {}),
        });
        this.enrollments = payload.data.results || [];
        this.totalEnrollments = payload.data.count || this.enrollments.length || 0;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },
    onPageChange(pageNum) {
      this.offset = (pageNum - 1) * this.perPage;
      this.fetchEnrollments();
    },

    onSearch() {
      this.offset = 0;
      this.debouncedSearch(this);
    },
    debouncedSearch: debounce(vm => {
      vm.fetchEnrollments();
    }, 500),
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    resolveSchoolName(item) {
      return (
        this.get(item, 'student_school.name') ||
        this.get(item, 'school_name') ||
        this.get(item, 'school.name') ||
        (typeof this.get(item, 'school') === 'string' ? this.get(item, 'school') : '') ||
        '-'
      );
    },
    handleRowClick(item) {
      const enrollmentId = this.get(item, 'id');

      if (!enrollmentId) {
        this.makeToast({ variant: 'warning', msg: 'Unable to open gradebook for this enrollment.' });
        return;
      }
      this.$router.push({
        name:
          this.getLoggedInUser.role === USER_ROLE_TYPES.SUPER_ADMIN
            ? 'sa-course-enrollment-gradebook'
            : 'course-enrollment-gradebook',
        params: { enrollmentId },
      });
    },
    rowClass() {
      return 'cursor-pointer';
    },
  },
  async mounted() {
    await this.fetchCourses();
    if (this.$route.params.id) {
      this.courseFilter = this.$route.params.id;
    }
    await this.fetchTags();
    await this.fetchEnrollments();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped lang="scss">
.badge {
  font-size: 0.85rem;
}
.table-nowrap {
  white-space: nowrap;
}

::v-deep .table-nowrap {
  white-space: nowrap;
}

::v-deep .table-nowrap table {
  border-collapse: separate;
  border-spacing: 0;
}

::v-deep .sticky-col {
  position: -webkit-sticky;
  position: sticky !important;
  background: #fff;
}

::v-deep .sticky-col-student {
  left: 0 !important;
}

::v-deep thead th.sticky-col-student {
  z-index: 5;
}

::v-deep tbody td.sticky-col-student {
  z-index: 3;
}

::v-deep thead th.sticky-col {
  background: #f8f9fa;
}

::v-deep .vs__dropdown-option {
  white-space: normal;
}

.course-opt {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;

  &__title {
    font-weight: 600;
  }
  &__tag {
    font-weight: 500;
    font-size: 0.65rem;
    letter-spacing: 0.2px;
  }
}
</style>
